import { FormattedMessage, useIntl } from 'react-intl';
import type { TravellerWithCollectionMembershipsDTO } from '@/api/trafikportalen';
import type { CollectionImportChangeDTO } from '@/api/trafikportalen';
import { useCallback } from 'react';
import { cn } from '@/helpers/classnames';
import { useImportFileStore } from '@/stores/importFile';
import {
  formatFieldName,
  parseErrorMessage,
  translateErrorMessage,
} from './importRowUtils';
import Button from '../core/Button';
import { getImportStateMeta, ImportState } from '@/helpers/import';
import { IconEdit } from '@/icons/CoreIcons';
import type { StudentType } from '@/types/students';

export interface ImportChangeWithTraveller {
  change: CollectionImportChangeDTO;
  traveller?: TravellerWithCollectionMembershipsDTO;
}

interface ChangeListItemProps {
  item: ImportChangeWithTraveller;
  fileRowNumber?: number;
  existingData?: Record<string, string>;
  isEditedInValidation?: boolean;
  displayAction?: string;
  studentType: StudentType;
  disableHalfProduct?: boolean;
  onOpen: () => void;
}

export const ImportRowListItem = ({
  item,
  fileRowNumber,
  existingData,
  isEditedInValidation,
  displayAction,
  studentType,
  disableHalfProduct = false,
  onOpen,
}: ChangeListItemProps) => {
  const intl = useIntl();
  const { importData } = useImportFileStore();

  const handleOpenModal = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onOpen();
    },
    [onOpen],
  );

  if (!item) return null;

  const { change, traveller } = item;
  const travellerData = traveller?.traveller;

  const row = importData.find((r) => r.row_number === change.row_number);

  const displayName = travellerData
    ? `${travellerData.first_name ?? ''} ${travellerData.last_name ?? ''}`.trim()
    : row?.first_name && row?.last_name
      ? `${row.first_name} ${row.last_name}`
      : existingData?.first_name && existingData?.last_name
        ? `${existingData.first_name} ${existingData.last_name}`
        : existingData?.name || null;

  const displaySsn =
    travellerData?.national_identity_number ??
    row?.national_identity_number ??
    existingData?.national_identity_number ??
    null;

  return (
    <Button
      onClick={handleOpenModal}
      className={cn(
        'w-full flex-col items-start px-4 py-2 text-left text-base',
      )}
      aria-label={intl.formatMessage(
        { defaultMessage: 'Rad {rowNumber}: {name}' },
        {
          rowNumber: fileRowNumber ?? '–',
          name:
            displayName ||
            intl.formatMessage({ defaultMessage: 'Namn saknas' }),
        },
      )}
    >
      <div className="flex w-full flex-wrap gap-x-5">
        <span className="min-w-8 text-text-700">
          {fileRowNumber != null ? fileRowNumber : '–'}
        </span>
        <div className="flex flex-1 flex-wrap gap-x-5">
          {displaySsn && (
            <span className="text-nowrap font-mono text-text-700">
              {displaySsn}
            </span>
          )}
          <div className="flex flex-1 flex-wrap whitespace-nowrap text-nowrap">
            {displayName ? (
              <span className="text-text-950">{displayName}</span>
            ) : (
              <span className="italic text-text-500">
                <FormattedMessage defaultMessage="Namn saknas" />
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-2">
          {isEditedInValidation && (
            <span
              className="flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-xs text-yellow-800"
              title={intl.formatMessage({
                defaultMessage: 'Manuellt redigerad under validering',
              })}
            >
              <IconEdit className="size-3" aria-hidden="true" />
              <FormattedMessage defaultMessage="Redigerad" />
            </span>
          )}
          <span
            className={cn(
              'text-base',
              getImportStateMeta(
                (displayAction ?? change.action) as ImportState,
              ).textColor,
            )}
          >
            <FormattedMessage
              id={`import.state.${displayAction ?? change.action}`}
            />
          </span>
        </div>
      </div>
      {change.error_message && (
        <span className="ml-14 text-red-700">
          {parseErrorMessage(change.error_message)
            .map(
              (error) =>
                `${formatFieldName(error.field, {
                  studentType,
                  disableHalfProduct,
                })}: ${translateErrorMessage(intl, error)}`,
            )
            .join(', ')}
        </span>
      )}
    </Button>
  );
};
